import * as Yup from "yup";
import { ButtonProps } from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { DatePicker } from "../../../components/DatePicker";
import { Dialog } from "../../../components/Dialog";
import { Campaign } from "../../../types/results";
import { useStyles } from "../styles";
import { parseISO } from "date-fns";
import { useMutation } from "react-query";
import { updateCampaign } from "../../../services/campaigns";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";

type DialogEditProps = {
  data: Campaign;
  open: boolean;
  onClose: () => void;
};

type FormValues = {
  id: number;
  name: string;
  startDate: Date;
  endDate: Date;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Nome da campanha inválido"),
  startDate: Yup.date().required("Data de início da campanha inválido"),
  endDate: Yup.date().required("Data de término da campanha inválido"),
});

export function DialogEdit({ data, open, onClose }: DialogEditProps) {
  const styles = useStyles();
  const toast = useToast();

  const putCampaign = useMutation(
    async (data: FormValues) => {
      const response = await updateCampaign(data);
      return response;
    },
    {
      onSuccess: (message) => {
        toast.show(message, {
          severity: "success",
        });
        queryClient.invalidateQueries(["results"]);
        queryClient.invalidateQueries(["rescue"]);
        onClose();
      },
      onError: () => {
        toast.show("Erro ao atualizar a campanha", { severity: "error" });
      },
    }
  );

  const handleSubmit = useCallback(
    async (data) => {
      await putCampaign.mutateAsync(data);
    },
    [putCampaign]
  );

  const formik = useFormik<FormValues>({
    initialValues: {} as FormValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Fechar",
        onClick: onClose,
      },
      {
        disabled: formik.isSubmitting,
        variant: "contained",
        children: formik.isSubmitting ? "Enviado solicitação" : "Editar",
        onClick: () => formik.handleSubmit(),
      },
    ];
  }, [onClose, formik]);

  useEffect(() => {
    formik.setFieldValue("id", data?.ID_CAMPANHA);
    formik.setFieldValue("name", data?.nome_campanha);
    formik.setFieldValue(
      "startDate",
      parseISO(String(data?.data_inicio_campanha)?.replace("Z", ""))
    );
    formik.setFieldValue(
      "endDate",
      parseISO(String(data?.data_fim_campanha)?.replace("Z", ""))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title={data?.nome_campanha}
      open={open}
      actions={actions}
    >
      <Stack className={styles.rootContent} spacing={4}>
        <TextField
          value={formik.values.name}
          variant="outlined"
          size="small"
          label="Nome"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("name", value)
          }
        />
        <Stack spacing={2} direction="row">
          <DatePicker
            fullWidth
            size="small"
            label="Data de início"
            onChange={(date) => formik.setFieldValue("startDate", date)}
            value={formik.values.startDate}
          />
          <DatePicker
            fullWidth
            size="small"
            label="Data de término"
            onChange={(date) => formik.setFieldValue("endDate", date)}
            value={formik.values.endDate}
          />
        </Stack>
      </Stack>
    </Dialog>
  );
}
